/**
 * The day-timeline model — pure functions from the composed-day DTO to what
 * the page draws. No fetch, no React: `timeline-model.test.ts` exercises this
 * against fixtures, and `TimelinePage` only renders what comes out.
 *
 * ===========================================================================
 * THE ORDER OF EVENTS IS THE SERVER'S INSTANTS, NOT A NARRATIVE
 * ===========================================================================
 * The deliverable reads "clocked in → arrived → left → clocked out", and on a
 * normal day that is what sorting by instant produces. But days are not
 * normal: an engineer can arrive at a customer BEFORE clocking in, or forget
 * to clock out entirely. The timeline renders what the records say, in the
 * order they say it happened — re-ordering events to match the expected story
 * would fabricate a compliance the data does not show, which is the same
 * defect as auto-closing a forgotten clock-out. Ties (same second) fall back
 * to the story order so a simultaneous clock-in/arrive still reads sensibly.
 *
 * ===========================================================================
 * THE MAP IS AN HONEST PLOT, NOT A BASE MAP (recorded decision)
 * ===========================================================================
 * The repo has no map dependency (R2 measured none), and none is added: the
 * "map view" is an SVG plot of the day's RAW stops — numbered chronologically,
 * connected in time order, scaled to their own bounding box with a scale bar
 * derived from the latitude-corrected extent. It shows where the stops are
 * RELATIVE TO EACH OTHER, which is what a day review needs, and it cannot lie
 * about streets it does not know. Restricted (`'•••'`) and absent (`null`)
 * stamps are COUNTED, not plotted — a masked position must contribute zero
 * coordinate bytes to any channel, including this one's pixel coordinates.
 *
 * The projection is equirectangular with the longitude axis compressed by
 * cos(mid-latitude) — over a working day's extent (a city, at most a region)
 * the distortion is far below one pixel, and unlike a spherical projection
 * every step of it is checkable by hand in a test.
 */

import type { FieldTimeline, MaskedPosition, PositionView, VisitRow } from './api';
import { classifyPosition } from './position';

/* ─────────────────────────────────────────────────────────────────── events ── */

export type TimelineEventKind = 'clock-in' | 'arrive' | 'leave' | 'clock-out';

export interface TimelineEvent {
  kind: TimelineEventKind;
  /** SERVER instant, ISO-8601 UTC. */
  at: string;
  position: MaskedPosition;
  /** The visit an arrive/leave belongs to; undefined on the clock pair. */
  visit?: VisitRow;
}

/** Story order — the tie-break WITHIN one instant, never across instants. */
const KIND_ORDER: Record<TimelineEventKind, number> = {
  'clock-in': 0,
  arrive: 1,
  leave: 2,
  'clock-out': 3,
};

/** The day's events, oldest first. What the DTO does not carry does not appear. */
export function composeDayEvents(day: FieldTimeline): TimelineEvent[] {
  const events: TimelineEvent[] = [];
  if (day.attendance !== null) {
    events.push({
      kind: 'clock-in',
      at: day.attendance.clock_in_at,
      position: day.attendance.clock_in_position,
    });
    if (day.attendance.clock_out_at !== null) {
      events.push({
        kind: 'clock-out',
        at: day.attendance.clock_out_at,
        position: day.attendance.clock_out_position,
      });
    }
  }
  for (const visit of day.visits) {
    events.push({ kind: 'arrive', at: visit.arrive_at, position: visit.arrive_position, visit });
    if (visit.leave_at !== null) {
      events.push({ kind: 'leave', at: visit.leave_at, position: visit.leave_position, visit });
    }
  }
  return events.sort((a, b) => {
    const byInstant = Date.parse(a.at) - Date.parse(b.at);
    if (byInstant !== 0) return byInstant;
    return KIND_ORDER[a.kind] - KIND_ORDER[b.kind];
  });
}

/* ────────────────────────────────────────────────────────────────── stops ── */

export interface Stop {
  /** 1-based chronological number — what the plot and the event list both show. */
  seq: number;
  kind: TimelineEventKind;
  at: string;
  view: PositionView;
  /** Party name for a visit stop; null on the clock pair. */
  label: string | null;
}

export interface StopExtraction {
  /** Chronological, RAW positions only. */
  stops: Stop[];
  /** Events whose position came back `'•••'` — restricted, count stated. */
  restricted: number;
  /** Events whose position is `null` — never captured, or purged. */
  absent: number;
}

/**
 * The plottable subset of the day. Numbering is over the RAW stops (what the
 * plot shows), not over all events — a plot numbered 1,3,4 with an invisible
 * 2 would read as a rendering bug rather than a masked stamp; the counts say
 * what is not shown, in words, where the page states them.
 */
export function extractStops(events: readonly TimelineEvent[]): StopExtraction {
  const stops: Stop[] = [];
  let restricted = 0;
  let absent = 0;
  for (const event of events) {
    const state = classifyPosition(event.position);
    if (state.kind === 'raw') {
      stops.push({
        seq: stops.length + 1,
        kind: event.kind,
        at: event.at,
        view: state.view,
        label: event.visit?.party_name ?? null,
      });
    } else if (state.kind === 'restricted') {
      restricted += 1;
    } else {
      absent += 1;
    }
  }
  return { stops, restricted, absent };
}

/* ─────────────────────────────────────────────────────────────── projection ── */

export interface ProjectedStop extends Stop {
  x: number;
  y: number;
}

export interface StopPlot {
  width: number;
  height: number;
  points: ProjectedStop[];
  /**
   * Metres per SVG unit at the plot's scale — feeds the scale bar. `null` for
   * a single-stop plot, where extent (and therefore scale) does not exist.
   */
  metersPerUnit: number | null;
}

const EARTH_METERS_PER_DEGREE = 111_320;

/**
 * Fit the stops into a `width`×`height` box with `pad` around. Equirectangular
 * with cos(mid-lat) on the x axis; aspect preserved (one scale for both axes)
 * so a north-south leg is not stretched relative to an east-west one. A single
 * stop, or stops within ~metres of each other, centre in the box.
 */
export function projectStops(
  stops: readonly Stop[],
  width: number,
  height: number,
  pad: number,
): StopPlot {
  if (stops.length === 0) return { width, height, points: [], metersPerUnit: null };

  const lats = stops.map((stop) => Number(stop.view.lat));
  const lngs = stops.map((stop) => Number(stop.view.lng));
  const midLat = (Math.min(...lats) + Math.max(...lats)) / 2;
  const cosLat = Math.cos((midLat * Math.PI) / 180);

  const xs = lngs.map((lng) => lng * cosLat);
  const ys = lats.map((lat) => -lat);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);
  const extentX = maxX - minX;
  const extentY = maxY - minY;

  // Below ~1e-5 degrees (~1 m) the extent is GPS noise, not geometry.
  const DEGENERATE = 1e-5;
  if (extentX < DEGENERATE && extentY < DEGENERATE) {
    return {
      width,
      height,
      points: stops.map((stop) => ({ ...stop, x: width / 2, y: height / 2 })),
      metersPerUnit: null,
    };
  }

  const scale = Math.min(
    (width - 2 * pad) / Math.max(extentX, DEGENERATE),
    (height - 2 * pad) / Math.max(extentY, DEGENERATE),
  );
  // Centre the fitted extent along both axes.
  const offsetX = (width - extentX * scale) / 2;
  const offsetY = (height - extentY * scale) / 2;

  return {
    width,
    height,
    points: stops.map((stop, index) => ({
      ...stop,
      x: offsetX + ((xs[index] as number) - minX) * scale,
      y: offsetY + ((ys[index] as number) - minY) * scale,
    })),
    // One SVG unit = 1/scale degrees; a degree (on this corrected grid) is
    // ~111.32 km on both axes, which is the point of the cos correction.
    metersPerUnit: EARTH_METERS_PER_DEGREE / scale,
  };
}

/**
 * A round-number scale bar that fits inside `maxUnits` SVG units, or null when
 * the plot has no scale. "Round" = 1/2/5 × 10ⁿ metres — the only labels a
 * reader can hold in their head.
 */
export function scaleBar(
  metersPerUnit: number | null,
  maxUnits: number,
): { units: number; label: string } | null {
  if (metersPerUnit === null || metersPerUnit <= 0) return null;
  const maxMeters = metersPerUnit * maxUnits;
  const steps = [1, 2, 5];
  let best: number | null = null;
  for (let power = 0; power <= 6; power += 1) {
    for (const step of steps) {
      const meters = step * 10 ** power;
      if (meters <= maxMeters) best = meters;
    }
  }
  if (best === null) return null;
  const label = best >= 1000 ? `${String(best / 1000)} km` : `${String(best)} m`;
  return { units: best / metersPerUnit, label };
}
